import { Component, Inject, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { APP_CONFIG } from '../../../../../app.config';
import { AssistantPopoverService } from '../../../../../common/assistant-popover.service';
import { makeValidatorChanges } from '../../../../../common/formValidationHelper';
import { ValidationService } from '../../../../../common/validation.service';
import { ValueListsService } from '../../../../../common/value-lists.service';
import { loadValueLists } from '../../../../../common/valueListHelper';
import { AppConfig } from '../../../../../models/config/app-config';
import { verpflichtungserklaerungsgeberFieldDefinitions } from '../../../../../models/formControls/formFieldDefinitions';
import { ValueListItem } from '../../../../../models/rest/valuelist/valuelist';
import { VisumErfassungSubFormComponent } from '../../../visum-erfassung-sub-form/visum-erfassung-sub-form.component';

@Component({
  selector: 'app-abweichender-verpflichtungsgeber',
  templateUrl: './abweichender-verpflichtungsgeber.component.html',
  styleUrls: ['./abweichender-verpflichtungsgeber.component.scss']
})
export class AbweichenderVerpflichtungsgeberComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();

  landListItems: ValueListItem[];
  staatsangehoerigkeitListItems: ValueListItem[];
  abweichenderVerpflichtungsgeberListItems: ValueListItem[];
  geschlechtListItems: ValueListItem[];
  registerListItems: ValueListItem[];
  wirkungsbereichListItems: ValueListItem[];

  private createForm(fb: FormBuilder): FormGroup {
    const controlsConfig : {} = {};

    verpflichtungserklaerungsgeberFieldDefinitions.forEach(field => {
      const validators = field.validatorParams
        ? this.validationService.getValidators(field.validatorPath, ...field.validatorParams)
        : this.validationService.getValidators(field.validatorPath);

      controlsConfig[field.controlName] = [null, validators];
    });

    return fb.group(controlsConfig);
  }

  constructor(fb: FormBuilder,
              private valueListsService: ValueListsService,
              private validationService: ValidationService,
              assistantPopoverService: AssistantPopoverService,
              @Inject(APP_CONFIG) appConfig: AppConfig,
              @Optional() @Self() controlDir: NgControl) {
    super(controlDir, assistantPopoverService);
    this.form = this.wrapFormGroup(this.createForm(fb));
  }

  private readonly REQUIRED_FIELDS_FOR_PERSON : string[] = [
    'familienname',
    'vorname',
    'geburtsdatum',
    'geburtsort',
    'land',
    'geschlecht',
    'staatsangehoerigkeit'
  ];

  private readonly REQUIRED_FIELDS_FOR_ORGANIZATION : string[] = [
    'familienname',
    'vorname',
    'land'
  ];

  private readonly REQUIRED_FIELDS_FOR_DIPLOMAT : string[] = [
    'land'
  ];

  ngOnInit() {
    super.ngOnInit();

    const valueLists = [
      { name: 'land', property: 'landListItems' },
      { name: 'staatsangehoerigkeit', property: 'staatsangehoerigkeitListItems' },
      { name: 'verpflichtungserklaerungsgeber_art', property: 'abweichenderVerpflichtungsgeberListItems' },
      { name: 'geschlecht', property: 'geschlechtListItems' },
      { name: 'organisation_register', property: 'registerListItems' },
      { name: 'organisation_wirkungsbereich', property: 'wirkungsbereichListItems' },
    ];

    loadValueLists(this.valueListsService, valueLists, this, this.subscriptions);

    // Sonderfallbehandlung wird manuel hinzugefügt
    this.subscriptions.add(
      this.form.controls.art.valueChanges.subscribe(value => {
        this.enableOrDisableControls(value);
        makeValidatorChanges(this.form, this.validationService, value, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
      })
    );
    makeValidatorChanges(this.form, this.validationService, this.form.value.art, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onValidatorUpdate() {
    this.enableOrDisableControls(this.form.value.art);
    makeValidatorChanges(this.form, this.validationService, this.form.value.art, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
  }

  private enableOrDisableControls(art: string) {
    switch (art) {
      case '2':
      case '3': { // "Organisation/Firma" oder "Akkreditierter Diplomat"
        const orgaName: AbstractControl = this.form.controls.orgaName;
        this.enableOrDisableControlWithTimeout(true, orgaName);
        break;
      }
      default: { // "Person"
        const orgaName: AbstractControl = this.form.controls.orgaName;
        this.enableOrDisableControlWithTimeout(false, orgaName);
        break;
      }
    }
  }
}
